import React, { ChangeEvent, ReactNode } from "react";
import i18n from "@i18n";
import { addMonths, LONG_DATE_FORMAT } from "@utils/datetime";
import { recommendPlan } from "@utils/calculateGoals";
import { trackEvent } from "@utils/api/tracker";
import RadioWithInputOther, {
  RadioWithInputOtherQuestionDefinition,
} from "../shared-inputs/RadioWithInputOther";
import Question from "../../../question-types/Question";

import styles from "./Dates.module.less";
import "react-datepicker/dist/react-datepicker.css";
import {
  SurveyAnswer,
  SurveyAnswersState,
} from "src/utils/redux/slices/surveyAnswers";
import {
  CustomQuestionDefinition,
  QuestionProps,
  registerQuestion,
} from "src/components/refactored-survey/QuestionDefinition";
import {
  question,
  questionTitle,
  questionOptions,
} from "@components/refactored-survey/question-types/Question.module.less";
import Button from "src/design-system/components/Button/Button";
import DateInput from "src/design-system/components/DateInput/DateInput";
import { getLocale } from "src/utils/meristemContext";

type DateQuestionDefinition = RadioWithInputOtherQuestionDefinition & {
  question: (surveyAnswer: SurveyAnswer) => ReactNode;
};

class ImportantDateDetails extends RadioWithInputOther<DateQuestionDefinition> {
  override handleClick(e) {
    const ans: SurveyAnswer = {};

    ans[this.props.question.id] = [e.target.value];
    const answerIsValid = this.validateAnswer(ans);
    ans.importantDateSubDetails = undefined;
    ans.importantDate = e.target.value === "none" ? ["no"] : ["yes"];
    this.props.registerAnswer(ans, answerIsValid);
  }

  override handleOtherInputChange(e: ChangeEvent<HTMLInputElement>) {
    super.handleOtherInputChange(e);

    const { value } = e.target;
    this.setState({ otherInput: value });
    const ans = { [this.props.question.id]: [this.otherFieldId, value] };
    const isValid = this.validateAnswer(ans);

    ans.importantDateSubDetails = [value];
    ans.importantDate = ["yes"];
    this.props.registerAnswer(ans, isValid);
  }
}

const OPTION_KEYS = [
  "vacation",
  "wedding",
  "holiday",
  "summer",
  "reunion",
  "birthday",
];

export function getImportantEventText(surveyAnswers: SurveyAnswersState) {
  let answer = surveyAnswers.importantDateDetails?.[0];
  if (!OPTION_KEYS.includes(answer)) {
    answer = "unknown";
  }

  return i18n.t(`survey:questions:importantDate:options:${answer}`);
}

export function getImportantEventTextLong(surveyAnswers: SurveyAnswersState) {
  let answer = surveyAnswers.importantDateDetails?.[0];
  if (!OPTION_KEYS.includes(answer)) {
    answer = "unknown";
  }

  return i18n.t(`survey:questions:importantDate:importantDateLong:${answer}`);
}

export const Q_IMPORTANT_DATE_DETAILS =
  registerQuestion<RadioWithInputOtherQuestionDefinition>({
    id: "importantDateDetails",
    type: "custom",
    component: ImportantDateDetails,
    question: () => {
      return (
        <>
          <h1 className={questionTitle}>
            {i18n.t("survey:questions:importantDate:title")}
          </h1>
          <h2 className={questionTitle}>
            {i18n.t("survey:questions:importantDate:question")}
          </h2>
        </>
      );
    },
    options: OPTION_KEYS.map((key) => ({
      id: key,
      text: i18n.t(`survey:questions:importantDate:options:${key}`),
    })),
    belowOtherOptions: [
      {
        id: "none",
        text: i18n.t("survey:questions:importantDate:options:none"),
      },
    ],
    section: 1,
  });

export class DatePickerQuestion extends Question<CustomQuestionDefinition> {
  constructor(props: QuestionProps) {
    super(props);

    const storeDate = this.props.surveyAnswers?.importantDateTime;
    this.state = {
      date: storeDate ? new Date(storeDate) : null,
    };

    this.onDateChange = this.onDateChange.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
  }

  // eslint-disable-next-line class-methods-use-this
  override onMount() {
    // Hack to disable keyboard pop up on mobile when user clicks on date field
    // https://stackoverflow.com/questions/48805053/hide-native-keyboard-on-mobile-when-using-react-date-picker
    const datePickers = document.getElementsByClassName(
      "react-datepicker__input-container"
    );
    Array.from(datePickers).forEach((el) => {
      if (el.childNodes[0]) {
        (el.childNodes[0] as HTMLElement).setAttribute("readOnly", "readOnly");
      }
    });
  }

  onDateChange(date: Date) {
    this.setState({ date }, () => {
      const { recommendedPlanDuration } = recommendPlan();
      const planEndDate = addMonths(new Date(), recommendedPlanDuration);
      trackEvent("ImportantDateComparedToPlanDate", {
        date: date > planEndDate ? "after" : "before",
      });

      const formattedDate = date
        ? new Date(date).toLocaleDateString(getLocale(), LONG_DATE_FORMAT)
        : null;
      this.props.registerAnswer({ importantDateTime: formattedDate }, !!date);
    });
  }

  onClearClick() {
    this.setState({ date: null });
  }

  override validateAnswer() {
    return !!this.state.date;
  }

  handleSkip() {
    trackEvent("SkippedImportantDateTime");
    this.props
      .registerAnswer({ importantDateTime: null }, true)
      .then(this.props.onClickNext);
  }

  override renderControls() {
    return (
      <>
        {super.renderControls()}
        <Button
          className={styles.datePickerSkip}
          onClick={this.handleSkip}
          type="button"
          designStyle="secondaryCta"
        >
          {i18n.t("survey:questions:importantDate:skipQuestion")}
        </Button>
      </>
    );
  }

  override renderWithControls() {
    // Prevent user from selecting a date in the past.
    const minDate = new Date();

    return (
      <div
        data-cy="question"
        data-cy-question={this.props.question.id}
        className={question}
      >
        {this.renderQuestion()}
        <div className={questionOptions}>
          <DateInput
            selectedDate={this.state.date}
            onDateChange={this.onDateChange}
            onClearClick={this.onClearClick}
            minDate={minDate}
            dateFormat="MMMM d yyyy"
            placeholderText={i18n.t(
              "survey:questions:importantDate:inputPlaceholder"
            )}
            data-cy="important-date-picker"
          />
        </div>
      </div>
    );
  }
}

export const Q_IMPORTANT_DATE_TIME = registerQuestion({
  id: "importantDateTime",
  type: "custom",
  component: DatePickerQuestion,
  question: ({ surveyAnswers }) => {
    let answer = surveyAnswers.importantDateDetails?.[0];
    if (!OPTION_KEYS.includes(answer)) {
      answer = "unknown";
    }

    return i18n.t(`survey:questions:importantDateTime:${answer}`);
  },
  section: 1,
});
